import type { AxiosInstance, AxiosResponse } from 'axios';
import type { GuestsImportedBucket, PortalSelectionArea, TrackingEventMap, TrackingEventName } from './events';
import { isPortalTrackingActive, trackPortal } from './portal';

// Client-portal events that are one request each (a selection saved, a PDF downloaded...), sent from the API client
// after the request succeeds. Every caller (legacy tabs, Cotiza v2 screens, dialogs shared by both) goes through
// apiClient, so the route table below is the only place they are tracked. Same gate as every portal event: nothing is
// sent unless trackPortal is active (a client whose lead authorized tracking).

type PortalEvent = { [E in TrackingEventName]: { event: E; props: TrackingEventMap[E] } }[TrackingEventName];

interface Route {
  method: string;
  path: RegExp;
  toEvent: (res: AxiosResponse) => PortalEvent | null;
}

const ID = '[^/?]+';

const selection = (area: PortalSelectionArea, action: 'add' | 'update' | 'remove') => (): PortalEvent => ({
  event: 'Portal Selection Changed',
  props: { area, action },
});

const SELECTION_ROUTES = (
  [
    ['event-selections', 'location'],
    ['event-menu-items', 'menu'],
    ['event-services', 'service'],
  ] as const
).flatMap(([base, area]): Route[] => [
  { method: 'post', path: new RegExp(`^/${base}$`), toEvent: selection(area, 'add') },
  { method: 'patch', path: new RegExp(`^/${base}/custom/${ID}/(quantity|hours)$`), toEvent: selection(area, 'update') },
  { method: 'patch', path: new RegExp(`^/${base}/(?!custom/)${ID}$`), toEvent: selection(area, 'update') },
  { method: 'delete', path: new RegExp(`^/${base}/(?!event/)${ID}$`), toEvent: selection(area, 'remove') },
]);

const PDF_GET = ['event-summary', 'guest-list', 'room-distribution', 'payment-schedule', 'contract', 'event-lodging', 'timeline', 'waiter-schedule'];
const PDF_POST = ['event-locations', 'event-food', 'event-services', 'event-full-summary', 'event-casa-digital'];

const pdf = (method: string, name: string): Route => ({
  method,
  path: new RegExp(`^/pdf/${name}/${ID}$`),
  toEvent: () => ({ event: 'PDF Exported', props: { pdf: name } }),
});

export const guestsImportedBucket = (count: number): GuestsImportedBucket => {
  if (count <= 0) return '0';
  if (count <= 25) return '1-25';
  if (count <= 50) return '26-50';
  if (count <= 100) return '51-100';
  return '100+';
};

const parseBody = (data: unknown): Record<string, unknown> | undefined => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as Record<string, unknown>;
    } catch {
      return undefined;
    }
  }
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : undefined;
};

const ROUTES: Route[] = [
  ...SELECTION_ROUTES,
  ...PDF_GET.map((name) => pdf('get', name)),
  ...PDF_POST.map((name) => pdf('post', name)),
  {
    method: 'post',
    path: new RegExp(`^/guests/import-csv/${ID}$`),
    toEvent: (res) => ({
      event: 'Guests Imported',
      props: { count_bucket: guestsImportedBucket(Number((res.data as { imported?: number })?.imported ?? 0)) },
    }),
  },
  {
    method: 'patch',
    path: new RegExp(`^/events/${ID}/feature-flags$`),
    // Clients can't disable guest management, so only enabling is tracked
    toEvent: (res) => {
      const body = parseBody(res.config.data);
      if (body?.guest_management_enabled === true)
        return { event: 'Portal Module Enabled', props: { module: 'guest_management' } };
      if (body?.digital_invitations_enabled === true)
        return { event: 'Portal Module Enabled', props: { module: 'digital_invitations' } };
      return null;
    },
  },
  {
    method: 'post',
    path: new RegExp(`^/events/${ID}/contracts/${ID}/signed$`),
    toEvent: () => ({ event: 'Signed Contract Uploaded', props: {} }),
  },
];

/** The request's path relative to the API base, without its query. */
const pathOf = (url: string | undefined): string => {
  if (!url) return '';
  const noQuery = url.split('?')[0];
  try {
    // Absolute URLs (rare) keep only the part after the API version prefix
    const { pathname } = new URL(noQuery);
    return pathname.replace(/^.*\/api\/v\d+/, '');
  } catch {
    return noQuery.startsWith('/') ? noQuery : `/${noQuery}`;
  }
};

/** The portal event a successful response stands for, if any. */
export function portalEventFor(res: AxiosResponse): PortalEvent | null {
  const method = (res.config.method ?? 'get').toLowerCase();
  const path = pathOf(res.config.url);
  const route = ROUTES.find((r) => r.method === method && r.path.test(path));
  return route ? route.toEvent(res) : null;
}

/** Tracks the portal events of successful responses on `client`. Never changes or delays the response. */
export function installPortalRequestTracking(client: AxiosInstance): void {
  client.interceptors.response.use((res) => {
    if (isPortalTrackingActive()) {
      try {
        const hit = portalEventFor(res);
        if (hit) trackPortal(hit.event, hit.props as never);
      } catch {
        // analytics must never break a request
      }
    }
    return res;
  });
}
